import { observer } from "mobx-react-lite";
import React from "react";
import { useApp } from "../../../context";
import { useNavigate } from "react-router";
import WaveSurfer from "wavesurfer.js";
import { animated, useSprings } from "react-spring";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Icon } from "@opendash/icons";
import Steps from "rc-steps";
import { getAlias } from "./alias";

const MAX_MULTIPLIER = 3;
const DECAY_SECONDS = 45;
const TOTAL_SLIDES = 6; // 5 questions + 1 results slide — fixed for useSprings
const SNIPPET_DURATION = 5;
const HINT_COST = 50;

const STREAK_KEY = "antiscam_daily_streak";
const HISTORY_KEY = "antiscam_daily_history";

function calcMultiplier(elapsed: number) {
  return Math.max(1, MAX_MULTIPLIER - (elapsed / DECAY_SECONDS) * (MAX_MULTIPLIER - 1));
}

function seededRand(seed: number): number {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

function getSnippetStart(date: string, questionId: string, duration: number): number {
  const max = Math.max(0, duration - SNIPPET_DURATION);
  return max > 0 ? seededRand(hashStr(date + questionId)) * max : 0;
}

function generateSessionId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const SESSION_KEY = "antiscam_daily_session";

function getOrCreateSessionId(): string {
  const stored = localStorage.getItem(SESSION_KEY);
  if (stored) return stored;
  const id = generateSessionId();
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

interface StreakData {
  lastPlayed: string;
  currentStreak: number;
  bestStreak: number;
}

function loadStreak(): StreakData {
  try {
    return JSON.parse(localStorage.getItem(STREAK_KEY) || "null") ?? { lastPlayed: "", currentStreak: 0, bestStreak: 0 };
  } catch {
    return { lastPlayed: "", currentStreak: 0, bestStreak: 0 };
  }
}

function updateStreak(date: string): StreakData {
  const prev = loadStreak();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split("T")[0];
  const newStreak = prev.lastPlayed === yesterdayStr || prev.lastPlayed === date
    ? prev.currentStreak + (prev.lastPlayed === date ? 0 : 1)
    : 1;
  const updated: StreakData = {
    lastPlayed: date,
    currentStreak: newStreak,
    bestStreak: Math.max(prev.bestStreak, newStreak),
  };
  localStorage.setItem(STREAK_KEY, JSON.stringify(updated));
  return updated;
}

interface DailyHistoryEntry {
  date: string;
  score: number;
  correct: number;
  results: boolean[];
}

function loadDailyHistory(): DailyHistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveDailyHistory(entry: DailyHistoryEntry) {
  const history = loadDailyHistory();
  const idx = history.findIndex((h) => h.date === entry.date);
  if (idx >= 0) history[idx] = entry; else history.unshift(entry);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 30)));
}

// No `synthetic` field — server withholds it until answers are locked in
interface DailyQuestion {
  objectId: string;
  name: string;
  audio?: { url: string; name: string };
}

interface LeaderboardEntry {
  rank: number;
  alias: string;
  score: number;
  correctCount: number;
}

const useStyles = makeStyles((theme) => ({
  questionContainer: {
    minHeight: "50vh",
    position: "relative",
    overflow: "hidden",
  },
  question: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "auto",
    minHeight: "100%",
  },
  questionInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "auto",
    minHeight: "100%",
  },
  questionContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(2, 0),
  },
  questionAudio: {
    width: "100%",
    marginBottom: 12,
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  audioControls: {
    display: "flex",
    gap: theme.spacing(1),
    margin: "8px auto 10px",
    width: "fit-content",
  },
  choiceButtons: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  choiceButton: {
    minWidth: "150px",
    padding: theme.spacing(1, 3),
  },
  aliasChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(0,0,0,0.06)",
    borderRadius: 20,
    padding: "4px 12px",
    fontFamily: "monospace",
    fontWeight: 700,
    fontSize: 14,
  },
}));

export const DailyRoute = observer(function DailyRoute() {
  const app = useApp();
  const navigate = useNavigate();
  const classes = useStyles();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [questions, setQuestions] = React.useState<DailyQuestion[]>([]);
  const [challengeDate, setChallengeDate] = React.useState("");

  const [alias] = React.useState(() => getAlias());

  const [index, setIndex] = React.useState(0);
  // Raw answers submitted to server — 1 = real, 2 = synthetic
  const [answers, setAnswers] = React.useState<Record<string, 1 | 2>>({});
  // Per-question elapsed seconds, sent to server for score calculation
  const [times, setTimes] = React.useState<Record<string, number>>({});
  const [hintsUsed, setHintsUsed] = React.useState<Record<string, boolean>>({});
  const [elapsed, setElapsed] = React.useState(0);

  // Results revealed only after server validates and responds
  const [serverScore, setServerScore] = React.useState<number | null>(null);
  const [serverCorrectCount, setServerCorrectCount] = React.useState<number | null>(null);
  const [serverResults, setServerResults] = React.useState<Record<string, boolean>>({});

  const [loadedCount, setLoadedCount] = React.useState(0);
  const [audioPlayback, setAudioPlayback] = React.useState<
    Record<string, { isPlaying: boolean; currentTime: number; duration: number }>
  >({});
  const [containerHeight, setContainerHeight] = React.useState(0);

  const [submitted, setSubmitted] = React.useState(false);
  const [submitWasNew, setSubmitWasNew] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [leaderboard, setLeaderboard] = React.useState<LeaderboardEntry[]>([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = React.useState(false);
  const [streakData, setStreakData] = React.useState<StreakData>(loadStreak());
  const [copied, setCopied] = React.useState(false);

  const wavesurferRefs = React.useRef<Record<string, WaveSurfer>>({});
  const containerRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const slideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = React.useRef(0);
  const snippetStartsRef = React.useRef<Record<string, number>>({});
  const sessionId = React.useRef(getOrCreateSessionId());

  React.useEffect(() => {
    app.parse
      .cc("daily-challenge-fetch")
      .then(({ result }: any) => {
        setQuestions(result.questions);
        setChallengeDate(result.date);
        setLoading(false);
      })
      .catch(() => {
        setError("Scamdle konnte nicht geladen werden.");
        setLoading(false);
      });
  }, []);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      Object.values(wavesurferRefs.current).forEach((ws) => ws.destroy());
    };
  }, []);

  React.useEffect(() => {
    if (loading || !questions.length) return;
    questions.forEach((question) => {
      if (!question?.audio?.url) return;
      if (wavesurferRefs.current[question.objectId]) return;
      const container = containerRefs.current[question.objectId];
      if (!container) return;

      const wavesurfer = WaveSurfer.create({
        container,
        waveColor: "#4fc3f7",
        progressColor: "#29b6f6",
        cursorColor: "#1976d2",
        barWidth: 2,
        height: 120,
        interact: false,
      });

      wavesurfer.load(question.audio.url);
      wavesurferRefs.current[question.objectId] = wavesurfer;

      wavesurfer.on("ready", () => {
        const duration = wavesurfer.getDuration();
        const snippetStart = getSnippetStart(challengeDate, question.objectId, duration);
        snippetStartsRef.current[question.objectId] = snippetStart;
        if (duration > 0) wavesurfer.seekTo(snippetStart / duration);
        setLoadedCount((prev) => prev + 1);
        setAudioPlayback((prev) => ({
          ...prev,
          [question.objectId]: { duration, currentTime: 0, isPlaying: false },
        }));
      });
      wavesurfer.on("play", () =>
        setAudioPlayback((prev) => ({ ...prev, [question.objectId]: { ...prev[question.objectId], isPlaying: true } }))
      );
      wavesurfer.on("pause", () =>
        setAudioPlayback((prev) => ({ ...prev, [question.objectId]: { ...prev[question.objectId], isPlaying: false } }))
      );
      wavesurfer.on("audioprocess", () => {
        const ct = wavesurfer.getCurrentTime();
        const snippetStart = snippetStartsRef.current[question.objectId] ?? 0;
        const relTime = ct - snippetStart;
        if (relTime >= SNIPPET_DURATION) {
          wavesurfer.pause();
          const dur = wavesurfer.getDuration();
          if (dur > 0) wavesurfer.seekTo(snippetStart / dur);
          setAudioPlayback((prev) => ({
            ...prev,
            [question.objectId]: { ...prev[question.objectId], currentTime: 0, isPlaying: false },
          }));
          return;
        }
        setAudioPlayback((prev) => ({
          ...prev,
          [question.objectId]: { ...prev[question.objectId], currentTime: Math.max(0, relTime) },
        }));
      });
    });
  }, [questions, loading]);

  React.useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    elapsedRef.current = 0;
    setElapsed(0);
    if (index < questions.length) {
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [index, questions.length]);

  React.useEffect(() => {
    const update = () => {
      let max = 0;
      slideRefs.current.forEach((ref) => { if (ref) max = Math.max(max, ref.scrollHeight); });
      if (max > 0) setContainerHeight(max);
    };
    const t = setTimeout(update, 100);
    window.addEventListener("resize", update);
    return () => { clearTimeout(t); window.removeEventListener("resize", update); };
  }, [index, audioPlayback, submitted, leaderboard, hintsUsed, serverResults]);

  const questionsWithAudio = questions.filter((q) => q.audio?.url).length;
  const allLoaded = !loading && (questionsWithAudio === 0 || loadedCount >= questionsWithAudio);
  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.objectId] !== undefined);
  const hintCount = Object.values(hintsUsed).filter(Boolean).length;

  const changeQuestion = (questionIndex: number) => {
    if (questions[index]) wavesurferRefs.current[questions[index].objectId]?.pause();
    setIndex(questionIndex);
    // @ts-ignore
    set((i: number) => ({ x: (i - questionIndex) * window.innerWidth }));
  };

  const handleAnswer = (questionIndex: number, answer: 1 | 2) => {
    const question = questions[questionIndex];
    if (!question || answers[question.objectId] !== undefined) return;
    if (timerRef.current) clearInterval(timerRef.current);

    wavesurferRefs.current[question.objectId]?.pause();
    setAnswers((prev) => ({ ...prev, [question.objectId]: answer }));
    setTimes((prev) => ({ ...prev, [question.objectId]: elapsedRef.current }));

    setTimeout(() => changeQuestion(questionIndex + 1), 600);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { result } = await app.parse.cc("daily-challenge-submit", {
        date: challengeDate,
        alias,
        answers,
        times,
        sessionId: sessionId.current,
        hintCount,
      });

      setServerScore(result.score);
      setServerCorrectCount(result.correctCount);
      setServerResults(result.results ?? {});
      setSubmitWasNew(true);
      setSubmitted(true);

      const updated = updateStreak(challengeDate);
      setStreakData(updated);
      saveDailyHistory({
        date: challengeDate,
        score: result.score,
        correct: result.correctCount,
        results: questions.map((q) => result.results?.[q.objectId] === true),
      });

      fetchLeaderboard();
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      if (msg.includes("already submitted")) {
        setSubmitted(true);
        fetchLeaderboard();
      } else {
        app.notifications.error("Score konnte nicht gespeichert werden.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fetchLeaderboard = async () => {
    setLoadingLeaderboard(true);
    try {
      const { result } = await app.parse.cc("daily-challenge-leaderboard");
      setLeaderboard(result);
    } catch {
      // optional, don't block
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const handleShare = () => {
    if (serverScore === null) return;
    const grid = questions
      .map((q) => (serverResults[q.objectId] ? "🟢" : "🔴"))
      .join("");
    const text = `Scamdle ${challengeDate}\n${grid} | ${serverScore} Pts\n${serverCorrectCount}/5 richtig → ${window.location.origin}/benchmark`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  React.useEffect(() => {
    if (index === questions.length && questions.length > 0 && !loadingLeaderboard && leaderboard.length === 0) {
      fetchLeaderboard();
    }
  }, [index, questions.length]);

  const [props, set] = useSprings(TOTAL_SLIDES, (i) => ({ x: i * window.innerWidth }));

  const dailyHistory = loadDailyHistory().filter((h) => h.date !== challengeDate).slice(0, 7);

  if (loading) {
    return (
      <Card style={{ padding: 40 }}>
        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 16 }}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">Scamdle wird geladen...</Typography>
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ padding: 40 }}>
        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 16 }}>
          <Typography color="error">{error}</Typography>
          <Button variant="outlined" onClick={() => navigate("/benchmark")}>Zurück</Button>
        </Box>
      </Card>
    );
  }

  const resultsSlide = (
    <div className={classes.questionInner}>
      <div className={classes.questionContent}>
        <Box display="flex" flexDirection="column" alignItems="center"
          style={{ gap: 20, padding: "32px 16px", maxWidth: 480, margin: "0 auto", width: "100%" }}>

          {/* Score — only shown after server confirms */}
          {submitted && serverScore !== null ? (
            <>
              <Typography variant="h2" style={{ fontSize: 64, lineHeight: 1 }}>
                {serverScore >= 400 ? "🏆" : serverScore >= 250 ? "🥇" : serverScore >= 150 ? "👍" : "📚"}
              </Typography>
              <Typography variant="h5" align="center" style={{ fontWeight: 700 }}>
                Scamdle abgeschlossen!
              </Typography>

              {streakData.currentStreak > 1 && (
                <Box style={{ background: "#fff3e0", borderRadius: 12, padding: "8px 20px", textAlign: "center" }}>
                  <Typography variant="body1" style={{ fontWeight: 700, color: "#e65100" }}>
                    🔥 {streakData.currentStreak} Tage in Folge
                  </Typography>
                  {streakData.bestStreak > streakData.currentStreak && (
                    <Typography variant="caption" color="textSecondary">Rekord: {streakData.bestStreak} Tage</Typography>
                  )}
                </Box>
              )}

              <Box style={{ background: "rgba(0,0,0,0.06)", borderRadius: 16, padding: "24px 40px", textAlign: "center", width: "100%" }}>
                <Typography variant="h3" style={{ fontWeight: 800 }}>{serverScore}</Typography>
                <Typography variant="subtitle1" color="textSecondary">Punkte</Typography>
                {hintCount > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    (inkl. −{hintCount * HINT_COST} Pkt. für {hintCount} Hinweis{hintCount > 1 ? "e" : ""})
                  </Typography>
                )}
                <Divider style={{ margin: "16px 0" }} />
                <Typography variant="h6" style={{ fontWeight: 700 }}>{serverCorrectCount} / {questions.length}</Typography>
                <Typography variant="body2" color="textSecondary">richtig erkannt</Typography>
              </Box>

              {/* Per-question dots — revealed after server responds */}
              <Box display="flex" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                {questions.map((q, qi) => {
                  const isCorrect = serverResults[q.objectId] === true;
                  return (
                    <Box key={q.objectId} onClick={() => changeQuestion(qi)}
                      title={`Frage ${qi + 1}: ${isCorrect ? "Richtig" : "Falsch"}`}
                      style={{
                        width: 44, height: 44, borderRadius: "50%",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        background: isCorrect ? "#4caf50" : "#f44336",
                        color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                      }}>
                      {qi + 1}
                    </Box>
                  );
                })}
              </Box>

              <Button variant="outlined" onClick={handleShare} style={{ minWidth: 180 }}>
                {copied ? "✓ Kopiert!" : "Ergebnis teilen"}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" align="center" style={{ fontWeight: 700 }}>
                Alle Fragen beantwortet!
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                Trage deinen Score ein — danach siehst du, welche Antworten richtig waren.
              </Typography>
              {/* Neutral dots before submission */}
              <Box display="flex" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                {questions.map((q, qi) => (
                  <Box key={q.objectId}
                    style={{
                      width: 44, height: 44, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: "rgba(0,0,0,0.12)", color: "#555", fontWeight: 700, fontSize: 13,
                    }}>
                    {qi + 1}
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Divider style={{ width: "100%" }} />

          {/* Alias + submission */}
          {!submitted ? (
            <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 12, width: "100%" }}>
              <Typography variant="body2" color="textSecondary">Du spielst als:</Typography>
              <span className={classes.aliasChip}>{alias}</span>
              <Button variant="contained" color="primary" disabled={submitting || !allAnswered}
                onClick={handleSubmit} style={{ marginTop: 4 }}>
                {submitting ? <CircularProgress size={20} /> : "Score eintragen & Auflösung sehen"}
              </Button>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 8 }}>
              {submitWasNew ? (
                <Typography variant="body2" style={{ color: "#4caf50", fontWeight: 700 }}>
                  ✓ Score eingetragen als <span style={{ fontFamily: "monospace" }}>{alias}</span>
                </Typography>
              ) : (
                <Typography variant="body2" style={{ color: "#ff9800", fontWeight: 700 }}>
                  Heute bereits eingereicht als <span style={{ fontFamily: "monospace" }}>{alias}</span>
                </Typography>
              )}
            </Box>
          )}

          {/* Leaderboard */}
          {submitted && (
            <Box style={{ width: "100%" }}>
              <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 12, textAlign: "center" }}>
                Heutige Top 25
              </Typography>
              {loadingLeaderboard ? (
                <Box display="flex" justifyContent="center"><CircularProgress size={24} /></Box>
              ) : leaderboard.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center">Noch keine Einträge.</Typography>
              ) : (
                <Box style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {leaderboard.map((entry) => (
                    <Box key={entry.rank} display="flex" alignItems="center" style={{
                      padding: "8px 12px", borderRadius: 8, gap: 12,
                      background: entry.alias === alias ? "rgba(25,118,210,0.12)" : "rgba(0,0,0,0.04)",
                      border: entry.alias === alias ? "1px solid #1976d2" : "1px solid transparent",
                    }}>
                      <Typography style={{ fontWeight: 700, minWidth: 24, color: entry.rank <= 3 ? "#f9a825" : undefined }}>
                        {entry.rank}.
                      </Typography>
                      <Typography style={{ flex: 1, fontFamily: "monospace", fontWeight: entry.alias === alias ? 700 : 400 }}>
                        {entry.alias}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
                        {entry.correctCount}/5
                      </Typography>
                      <Typography style={{ fontWeight: 700 }}>{entry.score} Pts</Typography>
                    </Box>
                  ))}
                  {!submitWasNew && serverScore !== null && (() => {
                    const projected = leaderboard.filter((e) => e.score > serverScore).length + 1;
                    return (
                      <Box display="flex" alignItems="center" style={{
                        padding: "8px 12px", borderRadius: 8, gap: 12,
                        background: "rgba(25,118,210,0.08)", border: "2px dashed #1976d2",
                      }}>
                        <Typography style={{ fontWeight: 700, minWidth: 24, color: "#1976d2" }}>{projected}.</Typography>
                        <Typography style={{ flex: 1, fontFamily: "monospace", fontWeight: 700, color: "#1976d2" }}>{alias} (Du)</Typography>
                        <Typography variant="body2" style={{ marginRight: 8, color: "#1976d2" }}>{serverCorrectCount}/5</Typography>
                        <Typography style={{ fontWeight: 700, color: "#1976d2" }}>{serverScore} Pts</Typography>
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </Box>
          )}

          {/* Past challenges */}
          {submitted && dailyHistory.length > 0 && (
            <Box style={{ width: "100%" }}>
              <Divider style={{ margin: "8px 0 12px" }} />
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Vergangene Scamdle</Typography>
              <Box style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {dailyHistory.map((h) => (
                  <Box key={h.date} display="flex" justifyContent="space-between" alignItems="center"
                    style={{ padding: "6px 10px", borderRadius: 8, background: "rgba(0,0,0,0.04)", fontSize: 13 }}>
                    <Typography variant="body2" color="textSecondary">{h.date}</Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", letterSpacing: 2 }}>
                      {(h.results || []).map((c) => (c ? "🟢" : "🔴")).join("")}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 700 }}>{h.score} Pts</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Button variant="outlined" onClick={() => navigate("/benchmark")} style={{ marginTop: 8 }}>
            Zurück zur Startseite
          </Button>
        </Box>
      </div>
    </div>
  );

  const slides = [
    ...questions.map((question, questionIndex) => {
      const isAnswered = answers[question.objectId] !== undefined;
      const playback = audioPlayback[question.objectId];
      const hintShown = !!hintsUsed[question.objectId];

      return (
        <div className={classes.questionInner}>
          <div className={classes.questionContent}>
            {/* Timer + multiplier */}
            <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
              <Typography variant="h6" style={{ fontFamily: "monospace" }}>
                ⏱ {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
              </Typography>
              {!isAnswered && (
                <Typography variant="body2" color="textSecondary" style={{ alignSelf: "center" }}>
                  {calcMultiplier(elapsed).toFixed(1)}× Multiplikator
                </Typography>
              )}
            </Box>

            {/* Neutral answered badge — no right/wrong revealed */}
            {isAnswered && (
              <Box mb={2} style={{
                padding: "8px 24px", borderRadius: 8,
                background: "#607d8b", color: "#fff", fontWeight: 700, fontSize: 18,
              }}>
                ✓ Beantwortet
              </Box>
            )}

            {question.audio?.url && (
              <div className={classes.questionAudio}>
                <div style={{ height: 0, overflow: "hidden" }}>
                  <div ref={(el) => { containerRefs.current[question.objectId] = el; }} style={{ width: "100%" }} />
                </div>
                {playback && (
                  <Box style={{ margin: "4px 8px 6px", height: 4, background: "#e0e0e0", borderRadius: 2 }}>
                    <Box style={{
                      height: "100%",
                      width: `${(Math.min(playback.currentTime, SNIPPET_DURATION) / SNIPPET_DURATION) * 100}%`,
                      background: "#29b6f6", borderRadius: 2, transition: "width 0.1s linear",
                    }} />
                  </Box>
                )}
                <div className={classes.audioControls}>
                  <Button variant="outlined" size="small" onClick={() => {
                    const ws = wavesurferRefs.current[question.objectId];
                    if (ws) playback?.isPlaying ? ws.pause() : ws.play();
                  }}>
                    {playback?.isPlaying ? "Pause" : "Play"}
                  </Button>
                  {!!playback && (
                    <Button variant="outlined" size="small" onClick={() => {
                      const ws = wavesurferRefs.current[question.objectId];
                      if (ws) {
                        const start = snippetStartsRef.current[question.objectId] ?? 0;
                        const dur = ws.getDuration();
                        if (dur > 0) ws.seekTo(start / dur);
                        setAudioPlayback((prev) => ({ ...prev, [question.objectId]: { ...prev[question.objectId], currentTime: 0 } }));
                      }
                    }}>
                      Restart
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Hint */}
            {!isAnswered && !hintShown && (
              <Button variant="text" size="small" style={{ color: "#ff9800", marginBottom: 8 }}
                onClick={() => setHintsUsed((prev) => ({ ...prev, [question.objectId]: true }))}>
                Hinweis anzeigen (−{HINT_COST} Pkt.)
              </Button>
            )}
            {hintShown && (
              <Box mb={1} style={{ padding: "6px 14px", borderRadius: 8, background: "#fff3e0", border: "1px solid #ffcc02" }}>
                <Typography variant="body2" style={{ color: "#e65100" }}>
                  💡 Achte auf Atemmuster, Pausen und die natürliche Sprachmelodie.
                </Typography>
              </Box>
            )}

            <Typography variant="subtitle2" align="center" gutterBottom>
              Ist diese Stimme echt oder KI-generiert?
            </Typography>
            <div className={classes.choiceButtons}>
              <Button className={classes.choiceButton}
                variant={answers[question.objectId] === 1 ? "contained" : "outlined"}
                color={answers[question.objectId] === 1 ? "primary" : "default"}
                disabled={isAnswered}
                onClick={() => handleAnswer(questionIndex, 1)}>
                Echt
              </Button>
              <Button className={classes.choiceButton}
                variant={answers[question.objectId] === 2 ? "contained" : "outlined"}
                color={answers[question.objectId] === 2 ? "primary" : "default"}
                disabled={isAnswered}
                onClick={() => handleAnswer(questionIndex, 2)}>
                KI-generiert
              </Button>
            </div>
          </div>
        </div>
      );
    }),
    resultsSlide,
  ];

  return (
    <Card style={{ padding: 20 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Scamdle · {challengeDate}
          </Typography>
          {streakData.currentStreak > 1 && (
            <Tooltip title={`${streakData.currentStreak} Tage in Folge (Rekord: ${streakData.bestStreak})`}>
              <Typography variant="body2" style={{ color: "#e65100", fontWeight: 700, cursor: "help" }}>
                🔥{streakData.currentStreak}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Tooltip title="Dein Name im Leaderboard">
          <span className={classes.aliasChip}>{alias}</span>
        </Tooltip>
      </Box>

      {!allLoaded && (
        <Box display="flex" flexDirection="column" alignItems="center" style={{ padding: "40px 16px", gap: 16 }}>
          <CircularProgress variant="determinate"
            value={questionsWithAudio > 0 ? Math.round((loadedCount / questionsWithAudio) * 100) : 0} />
          <Typography variant="body2" color="textSecondary">
            Audio wird geladen... {loadedCount} / {questionsWithAudio}
          </Typography>
        </Box>
      )}

      {allLoaded && (
        <Box display="flex" alignItems="center" mb={1} style={{ gap: 16 }}>
          <nav style={{ flex: 1, minWidth: 0 }}>
            <Steps current={index}>
              {questions.map((question, qi) => {
                const isAnswered = answers[question.objectId] !== undefined;
                return (
                  <Steps.Step
                    key={qi}
                    title=""
                    icon={isAnswered
                      ? <Icon icon="fa:check" />
                      : <Icon icon="fa:question-circle" />}
                    onClick={() => changeQuestion(qi)}
                    style={{ cursor: "pointer" }}
                  />
                );
              })}
            </Steps>
          </nav>
          <Typography variant="body2" color="textSecondary" style={{ whiteSpace: "nowrap" }}>
            {Object.keys(answers).length}/{questions.length}
          </Typography>
        </Box>
      )}

      <div className={classes.questionContainer}
        style={{ height: allLoaded ? containerHeight || "auto" : 1, visibility: allLoaded ? "visible" : "hidden" }}>
        {slides.map((slide, slideIndex) => {
          const { x } = props[slideIndex];
          return (
            <animated.div
              key={slideIndex}
              ref={(el) => { slideRefs.current[slideIndex] = el; }}
              className={classes.question}
              style={{ transform: x.interpolate((x) => `translate3d(${x}px,0,0)`) }}
              children={slide}
            />
          );
        })}
      </div>
    </Card>
  );
});
